import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Plus, Compass } from "lucide-react";
import { askAssistant } from "../../lib/adventureApi";

const SUGGESTIONS = [
  "Design a 2-week trip for me",
  "I'm a beginner — what's realistic?",
  "What should I add for culture?",
];

export default function AiAssistant({ region, trip, serviceLevel, modulesById, addedIds, onAdd }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi, I'm Sherpa — your trip designer. Tell me about your dream adventure and I'll recommend experiences to drop onto your map.", ids: [] },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(`sess-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = text ?? input;
    if (!msg.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const res = await askAssistant({
        session_id: sessionId.current,
        message: msg,
        region,
        trip,
        service_level: serviceLevel,
        current_module_ids: addedIds,
      });
      setMessages((m) => [...m, { role: "assistant", text: res.reply, ids: res.recommended_module_ids || [], sources: res.sources || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "I couldn't reach the mountain signal just now. Please try again.", ids: [] }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="ai-assistant-toggle"
        whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[1000] flex items-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white pl-4 pr-5 py-3.5 rounded-full shadow-xl"
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        <span className="font-semibold text-sm tracking-wide">Ask Sherpa</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
            transition={{ duration: 0.2 }}
            data-testid="ai-assistant-panel"
            className="fixed bottom-24 right-6 z-[1000] w-[92vw] max-w-sm h-[520px] bg-white border border-sand-dark rounded-md shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-2.5 p-4 bg-moss text-white">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center"><Compass className="w-4 h-4" /></div>
              <div>
                <p className="font-display font-bold text-sm tracking-tight">Sherpa</p>
                <p className="text-[11px] text-white/70">AI trip designer</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={m.role === "user" ? "flex justify-end" : ""}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 rounded-md text-sm leading-relaxed ${
                    m.role === "user" ? "bg-terracotta text-white rounded-br-none" : "bg-sand text-ink rounded-bl-none"
                  }`}>
                    {m.text}
                    {m.ids?.length > 0 && (
                      <div className="mt-2.5 space-y-1.5">
                        {m.ids.map((id) => {
                          const mod = modulesById[id];
                          if (!mod) return null;
                          const added = addedIds.includes(id);
                          return (
                            <button
                              key={id} data-testid={`ai-suggest-${id}`}
                              onClick={() => onAdd(mod)} disabled={added}
                              className={`flex items-center gap-2 w-full text-left px-2 py-1.5 rounded-sm bg-white text-ink text-xs font-medium border border-sand-dark ${added ? "opacity-60" : "hover:border-terracotta"}`}
                            >
                              <img src={mod.image} alt="" className="w-7 h-7 rounded-sm object-cover" />
                              <span className="flex-1">{mod.name}</span>
                              {!added && <Plus className="w-3.5 h-3.5 text-terracotta" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {m.sources?.length > 0 && (
                      <div className="mt-2.5 pt-2 border-t border-sand-dark/70" data-testid="ai-sources">
                        <p className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-1">From our journal</p>
                        <div className="space-y-1">
                          {m.sources.map((s) => (
                            <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer"
                              data-testid="ai-source-link"
                              className="block text-xs text-moss hover:text-terracotta underline underline-offset-2 leading-snug">
                              {s.title}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="bg-sand text-ink-muted text-sm px-3.5 py-2.5 rounded-md rounded-bl-none w-fit">Sherpa is thinking…</div>
              )}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)}
                      className="px-2.5 py-1 rounded-full text-xs border border-sand-dark text-ink-secondary hover:border-terracotta transition-colors">{s}</button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-3 border-t border-sand-dark flex items-center gap-2">
              <input
                data-testid="ai-input"
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about your trip…"
                className="flex-1 px-3 py-2 text-sm bg-[#FAFAFA] border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta"
              />
              <button data-testid="ai-send" onClick={() => send()} disabled={loading}
                className="p-2.5 bg-terracotta hover:bg-terracotta-hover text-white rounded-sm disabled:opacity-50">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
